import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Usuario } from "../model/usuario";
import { authService, SSOLoginData } from "../services/AuthService";
import { messageService } from "../services/MessageService";
import { useTranslation } from "react-i18next";

export interface LoginProps {
    login: string
    senha: string
}

interface AuthContextData {
    usuario: Usuario
    setUsuario: (usuario: Usuario) => void
    login: (props: LoginProps) => Promise<void>
    loginSSO: (data: SSOLoginData) => Promise<void>
    logout: () => void
    setOpenLicenseDialog: React.Dispatch<React.SetStateAction<boolean>>
    openLicenseDialog: boolean
}

export const STORAGE_USER_KEY = '@idus/cli/user-token'

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

export const AuthProvider: React.FC<any> = ({ children }) => {

    const { t } = useTranslation();

    const [usuario, setUsuario] = useState<Usuario>(new Usuario())
    const [openLicenseDialog, setOpenLicenseDialog] = useState<boolean>(false)

    useEffect(() => {
        const userData = localStorage.getItem(STORAGE_USER_KEY)
        if (userData) {
          const userAux = JSON.parse(userData)
          setUsuario(userAux)
          authService.setToken(`Bearer ${userAux.token}`)
        }
    }, [])

    const login = useCallback(async (data: LoginProps) => {
        try {
          const response = await authService.login(data)
    
          const iniciarSessao = async () => {
            localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(response.data))
            authService.setToken(`Bearer ${response.data.token}`)
            setUsuario(response.data)
          }

          iniciarSessao();
    
        } catch (error: any) {
            console.error(error)
            if (error.response && error.response.status === 404) {
                messageService.error(t('login.msg.genericError')) 
            } else {
                    const msgErro = error.response?.data?.detail || error.response?.data?.message || t('login.msg.invalidUserOrPassword');
                    messageService.error(msgErro)
            }
        }
    }, [t])

    const loginSSO = useCallback(async (data: SSOLoginData) => {
        try {
          const response = await authService.loginSSO(data)
    
          localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(response.data))
          authService.setToken(`Bearer ${response.data.token}`)
          setUsuario(response.data)
    
        } catch (error: any) {
            console.error(error)
            messageService.error(error.response?.data?.message || 'Erro ao fazer login com SSO')
        }
    }, [])

    const logout = useCallback(() => {
        setUsuario({} as Usuario)
        localStorage.removeItem(STORAGE_USER_KEY)
        authService.logout()
    }, [])

    return (
        <AuthContext.Provider
          value={{
            usuario,
            setUsuario,
            login,
            loginSSO,
            logout,
            setOpenLicenseDialog,
            openLicenseDialog
          }}
        >
          {children}
        </AuthContext.Provider>
      )

}

export function useAuth(): AuthContextData {
    const context = useContext(AuthContext)
    if (!context) {
      throw new Error('Não há contexto disponível para utilizar no hook')
    }
  
    return context
  }